import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, MoreVertical } from 'lucide-react';

function TabBar({ tabs, activeTabId, onTabClick, onTabClose, onCloseOthers, onCloseToRight, onCloseToLeft, onCloseAll }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // Update menu position when opening
  useEffect(() => {
    if (menuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 4,
        right: window.innerWidth - rect.right + window.scrollX
      });
    }
  }, [menuOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]);

  const activeTabIndex = tabs.findIndex(tab => tab.id === activeTabId);
  const canCloseToLeft = activeTabIndex > 0;
  const canCloseToRight = activeTabIndex < tabs.length - 1;

  const handleCloseOthers = () => {
    onCloseOthers();
    setMenuOpen(false);
  };

  const handleCloseToRight = () => {
    onCloseToRight();
    setMenuOpen(false);
  };

  const handleCloseToLeft = () => {
    onCloseToLeft();
    setMenuOpen(false);
  };

  const handleCloseAll = () => {
    onCloseAll();
    setMenuOpen(false);
  };

  return (
    <div className="bg-gray-200 flex items-center overflow-x-auto">
      <div className="flex items-end min-w-0 flex-1">
        {/* All Tabs in order */}
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          return (
            <div
              key={tab.id}
              className={`relative flex items-center border-r border-gray-300 px-4 py-2 min-w-0 ${
                isActive 
                  ? 'bg-white' 
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              <span 
                className={`whitespace-nowrap cursor-pointer ${
                  isActive 
                    ? 'text-primary font-medium' 
                    : 'text-gray-700'
                }`}
                onClick={() => onTabClick(tab.id)}
              >
                {tab.label}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTabClose(tab.id);
                }}
                className="ml-2 text-gray-500 hover:text-gray-700 flex-shrink-0"
              >
                <X className="w-3 h-3" />
              </button>
              {/* Teal underline for active tab */}
              {isActive && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"></div>
              )}
            </div>
          );
        })}
      </div>

      {/* Three Dots Menu */}
      <div className="px-2 py-2 flex-shrink-0 relative">
        <button 
          ref={buttonRef}
          onClick={() => setMenuOpen(!menuOpen)}
          className="text-gray-600 hover:text-gray-800"
        >
          <MoreVertical className="w-4 h-4" />
        </button>
        
        {menuOpen && createPortal(
          <div 
            ref={menuRef}
            className="fixed bg-white rounded-md shadow-lg border border-gray-200 py-1 z-[9999] min-w-[160px]"
            style={{
              top: `${menuPosition.top}px`,
              right: `${menuPosition.right}px`
            }}
          >
            <button
              onClick={handleCloseOthers}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <X className="w-3 h-3 mr-2" />
              <span>Close Others</span>
            </button>
            <button
              onClick={handleCloseToRight}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <X className="w-3 h-3 mr-2" />
              <span>Close to the Right</span>
            </button>
            <button
              onClick={handleCloseToLeft}
              disabled={!canCloseToLeft}
              className={`w-full flex items-center px-4 py-2 text-sm ${
                canCloseToLeft 
                  ? 'text-gray-700 hover:bg-gray-100' 
                  : 'text-gray-400 cursor-not-allowed'
              }`}
            >
              <X className={`w-3 h-3 mr-2 ${!canCloseToLeft ? 'opacity-50' : ''}`} />
              <span>Close to the Left</span>
            </button>
            <div className="border-t border-gray-200 my-1"></div>
            <button
              onClick={handleCloseAll}
              className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              <X className="w-3 h-3 mr-2" />
              <span>Close All</span>
            </button>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
}

export default TabBar;
